import { Fragment, useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';

import { cargarProveedoresProductoInventario, limpiarProveedoresProductoInventario } from '../../../actions/inventario';

const toNum = (v) => {
    const n = Number(v);
    return Number.isNaN(n) ? 0 : n;
};

const formatQty = (n) => {
    const x = toNum(n);
    return x.toFixed(2);
};

const money = (n) => {
    const x = toNum(n);
    return `$${new Intl.NumberFormat('es-MX', {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3
    }).format(x)}`;
};

const StockProductoModal = ({ show, onHide }) => {
    const dispatch = useDispatch();
    const { detalles, proveedoresProducto, loadingProveedoresProducto } = useSelector((state) => state.inventario);

    const unidades = useMemo(() => {
        const src = detalles?.producto?.unidades ?? detalles?.unidades ?? [];
        return Array.isArray(src) ? src : [];
    }, [detalles]);

    const inventarios = useMemo(() => {
        const src = detalles?.producto?.inventarios ?? detalles?.inventarios ?? [];
        return Array.isArray(src) ? src : [];
    }, [detalles]);

    const opcionesUnidad = useMemo(() => {
        return unidades
            .map((u) => ({
                value: u?.unidad?._id || '',
                label: u?.unidad?.nombre || 'Unidad',
                factor: toNum(u?.cantidad),
                tipo: toNum(u?.tipo)
            }))
            .filter((u) => u.value);
    }, [unidades]);

    const [unidadSel, setUnidadSel] = useState(null);

    useEffect(() => {
        if (!show) return;

        if (!opcionesUnidad.length) {
            setUnidadSel(null);
            return;
        }

        const principal = opcionesUnidad.find((u, idx) => {
            const raw = unidades[idx];
            return toNum(raw?.tipo) === 1 && toNum(raw?.cantidad) === 1;
        });

        setUnidadSel(principal || opcionesUnidad[0]);
    }, [show, opcionesUnidad, unidades]);

    const factorActual = unidadSel?.factor ?? 1;
    const nombreUnidadActual = unidadSel?.label || '';

    const rows = useMemo(() => {
        return inventarios.map((i) => {
            const base = toNum(i?.cantidad);
            const conv = base * factorActual;

            return {
                id: i?._id,
                area: i?.area?.nombre || 'Sin definir',
                organizador: i?.organizador?.nombre || 'Sin definir',
                ubicacion: i?.ubicacion?.nombre || 'Sin definir',
                cantidad: formatQty(conv),
                unidad: nombreUnidadActual
            };
        });
    }, [inventarios, factorActual, nombreUnidadActual]);

    const onChangeUnidad = (e) => {
        const v = e.target.value;
        const found = opcionesUnidad.find((u) => u.value === v) || null;
        setUnidadSel(found);
    };

    const nombreProducto = detalles?.producto?.nombre || detalles?.nombre || 'Producto';
    const idProducto = detalles?.producto?._id || detalles?._id || '';

    useEffect(() => {
        if (!show) return;

        if (idProducto) {
            dispatch(cargarProveedoresProductoInventario(idProducto));
        } else {
            dispatch(limpiarProveedoresProductoInventario());
        }

        return () => {
            dispatch(limpiarProveedoresProductoInventario());
        };
    }, [show, idProducto, dispatch]);

    const proveedoresRows = useMemo(() => {
        const src = Array.isArray(proveedoresProducto) ? proveedoresProducto : [];
        return src
            .filter((p) => !p?.deleted)
            .map((p) => ({
                id: p?._id || `${p?.proveedor?._id || ''}-${p?.producto || ''}`,
                proveedor: p?.proveedor?.nombre || 'Proveedor',
                precio_historico: money(p?.precio_historico),
                precio_publico: money(p?.precio_publico),
                precio_minimo: money(p?.precio_minimo),
                precio_liquidacion: money(p?.precio_liquidacion)
            }));
    }, [proveedoresProducto]);

    return (
        <Fragment>
            <Modal show={show} onHide={onHide} centered size="lg">
                <Modal.Header closeButton>
                    <div className="d-flex flex-column">
                        <Modal.Title>Cantidades existentes</Modal.Title>
                        <small className="text-muted">{nombreProducto}</small>
                    </div>
                </Modal.Header>

                <Modal.Body>
                    {opcionesUnidad.length > 0 && (
                        <div className="mb-3">
                            <div className="text-muted mb-1">Unidad</div>
                            <select
                                className="form-select"
                                value={unidadSel?.value || ''}
                                onChange={onChangeUnidad}
                            >
                                {opcionesUnidad.map((u) => (
                                    <option key={u.value} value={u.value}>
                                        {u.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div className="table-responsive">
                        <table className="table mb-0">
                            <thead>
                                <tr>
                                    <th>Área</th>
                                    <th>Organizador</th>
                                    <th>Ubicación</th>
                                    <th className="text-end">Cantidad</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.length === 0 && (
                                    <tr>
                                        <td colSpan="4" className="text-center py-4">
                                            Sin registros para mostrar
                                        </td>
                                    </tr>
                                )}

                                {rows.map((r) => (
                                    <tr key={r.id}>
                                        <td>{r.area}</td>
                                        <td>{r.organizador}</td>
                                        <td>{r.ubicacion}</td>
                                        <td className="text-end">
                                            {r.cantidad}{r.unidad ? ` ${r.unidad}` : ''}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-4">
                        <h6 className="mb-2">Proveedores</h6>

                        <div className="table-responsive">
                            <table className="table mb-0">
                                <thead>
                                    <tr>
                                        <th>Proveedor</th>
                                        <th className="text-end">P. Compra</th>
                                        <th className="text-end">P. Público</th>
                                        <th className="text-end">P. Mín</th>
                                        <th className="text-end">P. Liquidación</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loadingProveedoresProducto && (
                                        <tr>
                                            <td colSpan="5" className="text-center py-4">
                                                Cargando...
                                            </td>
                                        </tr>
                                    )}

                                    {!loadingProveedoresProducto && proveedoresRows.length === 0 && (
                                        <tr>
                                            <td colSpan="5" className="text-center py-4">
                                                Sin proveedores para mostrar
                                            </td>
                                        </tr>
                                    )}

                                    {!loadingProveedoresProducto && proveedoresRows.map((p) => (
                                        <tr key={p.id}>
                                            <td>{p.proveedor}</td>
                                            <td className="text-end">{p.precio_historico}</td>
                                            <td className="text-end">{p.precio_publico}</td>
                                            <td className="text-end">{p.precio_minimo}</td>
                                            <td className="text-end">{p.precio_liquidacion}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </Fragment>
    );
};

export default StockProductoModal;
